import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  blobErrorText,
  useGetFieldSiteQuery,
  useGetFieldElementsQuery,
  useGetFieldInspectionRequestsQuery,
  useGetFieldDailyReportsQuery,
  useGetSiteVolumeFactsQuery,
  useGetFieldAnomaliesQuery,
  useGetFieldPhotosQuery,
  useGetFieldSiteMembersQuery,
  useGetFieldTitulsQuery,
  useGetFieldTasksQuery,
  useCreateFieldTaskMutation,
  useSetFieldTaskStatusMutation,
  useSetFieldAnomalyDecisionMutation,
  useSetFieldSiteVkChatMutation,
  useLazyExportFieldReportsXlsxQuery,
  useLazyExportFieldVolumesXlsxQuery,
  useLazyExportFieldPhotoReportPdfQuery,
  useGetFieldMaterialReceiptsQuery,
  useUploadFieldMaterialReceiptMutation,
  useAttachFieldReceiptPassportMutation,
  useConfirmFieldMaterialReceiptMutation,
  useDeleteFieldMaterialReceiptMutation,
  useListMtoLotsQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  anomalyDecisionActions,
  anomalyDecisionView,
  anomalyFlags,
  anomalyKindLabel,
  anomalyLlmView,
  anomalySeverityLabel,
  anomalySeverityTone,
  elementStatusHint,
  elementStatusLabel,
  elementStatusManual,
  elementStatusTone,
  elementRegistryPage,
  filterElementRegistry,
  factNeedsReview,
  factQtyView,
  factReconcileFlags,
  factReconcileView,
  fmtFieldDate,
  photoCategoryLabel,
  photoFileBadge,
  photoIntegrityBadges,
  reportStatusLabel,
  reportStatusTone,
  volumeFactStatusLabel,
  volumeFactStatusTone,
  siteReadOnly,
  siteStatusLabel,
  siteStatusTone,
  taskStatusLabel,
  taskStatusTone,
  useFieldAccess,
  useSiteForeman,
  useCurrentUserId,
  type PhotoFileProblem,
  type PhotoIntegrityBadge,
} from '@entities/field';
import { useAgentPermission } from '@entities/user';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { RegistryPagination } from '@shared/ui/RegistryShell';
import { FieldPhotoThumb } from './FieldPhotoThumb';
import { InspectionRequestCard } from './InspectionRequestCard';
import { ElementPositionPicker } from './ElementPositionPicker';
import { ElementStatusControls } from './ElementStatusControls';
import { ProjectInBinBanner } from './ProjectInBinBanner';
import { FieldRdImportButton } from './FieldRdImportButton';
import { CreatePersonalReport } from './PersonalReportEntry';
import { reportAuthorLabel, titulLabel } from '@entities/field/tituls';

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SiteTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const SiteMeta = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.6;
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const RowTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const RowMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.55;
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const OkNote = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Кликабельная карточка рапорта (инлайн as={Link} в SC v6 не типизируется). */
const ReportCard = styled(Card).attrs({ as: Link })`
  display: block;
  text-decoration: none;
  color: inherit;
  transition:
    border-color 0.12s ease,
    transform 0.12s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const InlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const SectionTools = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const PhotoMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  line-height: 1.5;
`;

const PhotoFlags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Chip = styled.button<{ $active: boolean }>`
  font-size: 12.5px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  background: transparent;
  cursor: pointer;
`;

const VkForm = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  max-width: 520px;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/** Замечания разбора фото по одному кадру (КW4) — списком, а не строкой через «; ». */
const FlagList = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme }) => theme.spacing(5)};

  & li + li {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

/** Мнение фильтра ИИ о находке — отдельной строкой, чтобы не путать с её текстом. */
const LlmNote = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  border-left: 3px solid ${({ theme }) => theme.colors.status.info};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Площадка не открылась целиком (нет доступа, не найдена) — одно сообщение вместо разделов. */
const DeniedBox = styled(Card)`
  max-width: 640px;
  font-size: 14px;
  line-height: 1.6;
`;

/** Скачивание blob как файла (образец MeetingDetail). */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Категории фото МП-3 для фильтра галереи. */
const PHOTO_CATEGORIES = [
  'hidden_works',
  'progress',
  'incoming',
  'defect',
  'document',
  'other',
] as const;

/** Сколько превью показывать за раз (догрузка кнопкой). */
const PHOTO_PAGE = 24;

/** Ответ запроса раздела: загрузка → ошибка (с причиной) → пусто → данные. Отказ
 *  в доступе — «нет доступа к …», а не «недостаточно прав для этого действия». */
function sectionState(
  q: { isLoading: boolean; isError: boolean; error?: unknown },
  what: string,
): string | null {
  if (q.isLoading) return 'Загрузка…';
  if (q.isError) {
    return apiLoadErrorText(
      q.error,
      `Не удалось загрузить ${what}.`,
      'Нет доступа к этому разделу.',
    );
  }
  return null;
}

/** Метки фото в галерее: файл не открылся — первой, затем флаги достоверности. */
function photoBadges(
  p: Parameters<typeof photoIntegrityBadges>[0],
  problem: PhotoFileProblem | undefined,
): PhotoIntegrityBadge[] {
  const file = photoFileBadge(problem);
  return file ? [file, ...photoIntegrityBadges(p)] : photoIntegrityBadges(p);
}

/**
 * Детальная страница площадки ОСНОВА.Прораб (`/agents/field/:siteId`) — обзор
 * для офиса и рабочее место проверяющего ПТО: заявки на освидетельствование с
 * действиями по статусам и сборкой АОСР, конструктив (в том числе из РД),
 * сменные рапорты, факты объёма, задачи, фотофиксация с флагами достоверности,
 * аномалии. Данные собираются в мобильном приложении. Проект ПТО площадки в
 * корзине — страница только для чтения (баннер, кнопок записи нет).
 */
export function FieldSiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  return <FieldSiteContent key={siteId ?? ''} siteId={siteId} />;
}

/** Смена площадки размонтирует все фильтры, формы и незавершённый выбор конструктива. */
function FieldSiteContent({ siteId }: { siteId: string | undefined }) {
  const id = siteId ?? '';
  const opts = { skip: !siteId };
  const pto = useAgentPermission('pto');
  const siteQ = useGetFieldSiteQuery(id, opts);
  // Права — по проекту площадки: проверяющий ПТО ведёт заявки, конструктив,
  // ручной статус элемента и канал VK, решает по аномалиям (КR1); задачи
  // ставит ведущий площадку; закрытие задач и «принять к сведению» аномалию —
  // любой, кто пишет на площадку (в том числе её прораб). Проект ПТО в корзине
  // — запись закрыта всем (сервер отвечает 409).
  const access = useFieldAccess(siteQ.data?.site.project_id);
  const foreman = useSiteForeman(siteId);
  const readOnly = siteReadOnly(siteQ.data?.site);
  const reviewer = access.reviewer && !readOnly;
  const canManage = access.manager && !readOnly;
  const siteWriter = (access.manager || foreman) && !readOnly;
  const elQ = useGetFieldElementsQuery(id, opts);
  const reqQ = useGetFieldInspectionRequestsQuery(id, opts);
  const userId = useCurrentUserId();
  const [reportOffset, setReportOffset] = useState(0);
  const [onlyMine, setOnlyMine] = useState(false);
  const repQ = useGetFieldDailyReportsQuery(
    { siteId: id, author_id: onlyMine ? userId : undefined, limit: 25, offset: reportOffset },
    opts,
  );
  const membersQ = useGetFieldSiteMembersQuery(id, opts);
  const titulsQ = useGetFieldTitulsQuery(siteQ.currentData?.site.project_id ?? '', {
    skip: !siteQ.currentData?.site.project_id,
  });
  const factsQ = useGetSiteVolumeFactsQuery(id, opts);
  const anQ = useGetFieldAnomaliesQuery(id, opts);
  const [photoCategory, setPhotoCategory] = useState<string | null>(null);
  const [photoOffset, setPhotoOffset] = useState(0);
  const [photoReport, setPhotoReport] = useState('');
  // Файлы, которые не открылись как изображение (выяснилось при показе превью).
  const [photoProblems, setPhotoProblems] = useState<Record<string, PhotoFileProblem>>({});
  const photoQ = useGetFieldPhotosQuery(
    {
      siteId: id,
      category: photoCategory ?? undefined,
      report_id: photoReport || undefined,
      limit: PHOTO_PAGE,
      offset: photoOffset,
    },
    opts,
  );
  const taskQ = useGetFieldTasksQuery(id, opts);
  const [createTask, { isLoading: taskCreating }] = useCreateFieldTaskMutation();
  const [setTaskStatus, { isLoading: taskSaving }] = useSetFieldTaskStatusMutation();
  const [taskText, setTaskText] = useState('');
  const [taskSource, setTaskSource] = useState('pto');
  const [taskRequiresPhoto, setTaskRequiresPhoto] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  // Ошибки действий по карточке — в самой карточке, а не над длинным списком.
  const [taskCloseError, setTaskCloseError] = useState<{ id: string; text: string } | null>(null);
  const [setAnomalyDecision, { isLoading: decisionSaving }] = useSetFieldAnomalyDecisionMutation();
  const [anomalyError, setAnomalyError] = useState<{ id: string; text: string } | null>(null);
  const [setVkChat, { isLoading: vkSaving }] = useSetFieldSiteVkChatMutation();
  const [vkChatDraft, setVkChatDraft] = useState<string | null>(null);
  const [vkNote, setVkNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [elementSearch, setElementSearch] = useState('');
  const [elementPageNumber, setElementPageNumber] = useState(1);
  const { setActiveProjectId } = useActiveProjectContext();
  const [exportReports, { isFetching: reportsExporting }] = useLazyExportFieldReportsXlsxQuery();
  const [exportVolumes, { isFetching: volumesExporting }] = useLazyExportFieldVolumesXlsxQuery();
  const [exportPhotoReport, { isFetching: photoReportExporting }] =
    useLazyExportFieldPhotoReportPdfQuery();

  const receiptsQ = useGetFieldMaterialReceiptsQuery(id, opts);
  const lotsQ = useListMtoLotsQuery(
    siteQ.data?.site.project_id ? { site_id: siteQ.data.site.project_id } : undefined,
    { skip: !siteQ.data?.site.project_id },
  );
  const [uploadReceipt, { isLoading: receiptUploading }] = useUploadFieldMaterialReceiptMutation();
  const [attachPassport, { isLoading: attachingPassport }] =
    useAttachFieldReceiptPassportMutation();
  const [confirmReceipt, { isLoading: confirmingReceipt }] =
    useConfirmFieldMaterialReceiptMutation();
  const [deleteReceipt, { isLoading: deletingReceipt }] =
    useDeleteFieldMaterialReceiptMutation();
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptElementId, setReceiptElementId] = useState('');
  const [receiptMsg, setReceiptMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const site = siteQ.data?.site;

  useEffect(() => {
    if (site?.project_id) {
      setActiveProjectId(site.project_id);
    }
  }, [site?.project_id, setActiveProjectId]);

  const allElements = elQ.data?.elements;
  const elements = useMemo(
    () => [...(allElements ?? [])].sort((a, b) => a.sort - b.sort),
    [allElements],
  );
  const filteredElements = useMemo(
    () => filterElementRegistry(elements, elementSearch),
    [elements, elementSearch],
  );
  const elementPage = elementRegistryPage(filteredElements, elementPageNumber);
  const requests = reqQ.data?.requests ?? [];
  const reports = repQ.currentData?.reports ?? [];
  const facts = factsQ.data?.facts ?? [];
  const anomalies = anQ.data?.anomalies ?? [];
  const tasks = taskQ.data?.tasks ?? [];
  const photos = photoQ.currentData?.photos ?? [];
  const receipts = receiptsQ.data?.receipts ?? [];
  const mtoLots = lotsQ.data?.items ?? [];
  const vkChatValue = vkChatDraft ?? site?.vk_chat_id ?? '';

  /** Выгрузка файла: причина сбоя — видимым сообщением, а не молчанием. */
  async function download(run: () => Promise<Blob>, filename: string, what: string) {
    setExportError(null);
    try {
      triggerDownload(await run(), filename);
    } catch (e) {
      setExportError(await blobErrorText(e, `Не удалось выгрузить ${what}.`));
    }
  }

  async function closeTask(taskId: string) {
    setTaskCloseError(null);
    try {
      await setTaskStatus({ taskId, status: 'done' }).unwrap();
    } catch (e) {
      setTaskCloseError({ id: taskId, text: apiErrorText(e, 'Не удалось закрыть задачу.') });
    }
  }

  async function decideAnomaly(anomalyId: string, decision: 'acknowledged' | 'rejected') {
    setAnomalyError(null);
    try {
      await setAnomalyDecision({ siteId: id, anomalyId, decision }).unwrap();
    } catch (e) {
      setAnomalyError({
        id: anomalyId,
        text: apiErrorText(e, 'Не удалось сохранить решение по аномалии.'),
      });
    }
  }

  const elState = sectionState(elQ, 'конструктив');
  const reqState = sectionState(reqQ, 'заявки');
  const repState = sectionState(repQ, 'рапорты');
  const anState = sectionState(anQ, 'аномалии');
  const taskState = sectionState(taskQ, 'задачи');
  const photoState = sectionState(photoQ, 'фото');
  const receiptsState = sectionState(receiptsQ, 'приёмки материалов');

  // Площадка не открылась (нет доступа, не найдена, сбой) — одно сообщение по делу,
  // а не та же ошибка в каждом из восьми разделов.
  if (siteQ.isError && !site) {
    const denied = apiErrorStatus(siteQ.error) === 403;
    return (
      <>
        <BackLink to="/agents/field">← К обзору</BackLink>
        <DeniedBox>
          {denied ? (
            <>
              <strong>Нет доступа к площадке.</strong> Площадку видят её участники (прораб,
              начальник участка) и проверяющие ПТО проекта. Если вы работаете на этом объекте,
              попросите инженера ПТО проекта назначить вас на площадку.
            </>
          ) : (
            apiErrorText(siteQ.error, 'Не удалось загрузить площадку.')
          )}
        </DeniedBox>
      </>
    );
  }

  return (
    <>
      <BackLink to="/agents/field">← К обзору</BackLink>

      {siteQ.isLoading && <Muted>Загрузка…</Muted>}

      {site && (
        <div>
          <ProjectInBinBanner site={site} />
          <HeadRow>
            <SiteTitle>{site.name}</SiteTitle>
            <Badge $tone={siteStatusTone(site.status)}>{siteStatusLabel(site.status)}</Badge>
            {readOnly && <Badge $tone="warning">только чтение</Badge>}
          </HeadRow>
          <SiteMeta>
            <strong>{titulLabel(titulsQ.currentData?.tituls ?? [], site.titul_id)}</strong>
            <br />
            {site.code && (
              <>
                Шифр: {site.code}
                <br />
              </>
            )}
            {site.location && (
              <>
                {site.location}
                <br />
              </>
            )}
            Часовой пояс: {site.timezone}
            {pto.can_read && (
              <>
                {' · '}
                <Link to={`/agents/pto/${site.project_id}`}>проект в ОСНОВА.Инженер →</Link>
              </>
            )}
            {!reviewer && (
              <>
                <br />
                Дайджесты в VK Teams:{' '}
                {site.vk_chat_id ? `канал ${site.vk_chat_id}` : 'канал не настроен'}
              </>
            )}
          </SiteMeta>
          {/* Канал дайджестов перенастраивает проверяющий ПТО, не прораб площадки. */}
          {reviewer && (
            <VkForm
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = vkChatValue.trim();
                setVkNote(null);
                setVkChat({ siteId: id, vkChatId: trimmed || null })
                  .unwrap()
                  .then(() => {
                    setVkChatDraft(null);
                    setVkNote({
                      ok: true,
                      text: trimmed
                        ? 'Канал дайджестов сохранён.'
                        : 'Дайджесты в VK Teams отключены.',
                    });
                  })
                  .catch((err: unknown) =>
                    setVkNote({
                      ok: false,
                      text: apiErrorText(err, 'Не удалось сохранить канал VK Teams.'),
                    }),
                  );
              }}
            >
              <Input
                value={vkChatValue}
                onChange={(e) => setVkChatDraft(e.target.value)}
                placeholder="VK Teams chat_id для дайджестов (пусто — отключить)"
                aria-label="VK Teams chat_id площадки"
              />
              <Button type="submit" $variant="secondary" $size="sm" disabled={vkSaving}>
                Сохранить
              </Button>
            </VkForm>
          )}
          {vkNote &&
            (vkNote.ok ? <OkNote>{vkNote.text}</OkNote> : <ErrorNote>{vkNote.text}</ErrorNote>)}
          <InlineActions>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={reportsExporting}
              onClick={() =>
                void download(() => exportReports(id).unwrap(), 'field_reports.xlsx', 'рапорты')
              }
            >
              Рапорты XLSX
            </Button>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={volumesExporting}
              onClick={() =>
                void download(() => exportVolumes(id).unwrap(), 'field_volumes.xlsx', 'объёмы')
              }
            >
              Объёмы XLSX
            </Button>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={photoReportExporting}
              onClick={() =>
                void download(
                  () =>
                    exportPhotoReport({
                      siteId: id,
                      category: photoCategory ?? undefined,
                    }).unwrap(),
                  'field_photo_report.pdf',
                  'фотоотчёт',
                )
              }
              title="Фото по текущему фильтру категории; водяные знаки — на страницах PDF"
            >
              Фотоотчёт PDF
            </Button>
          </InlineActions>
          {exportError && <ErrorNote>{exportError}</ErrorNote>}
        </div>
      )}

      <Section>
        <SectionLabel>Заявки на освидетельствование</SectionLabel>
        {reqState ? (
          <Muted>{reqState}</Muted>
        ) : requests.length === 0 ? (
          <Muted>Заявок нет.</Muted>
        ) : (
          <List>
            {requests.map((r) => {
              const el = elements.find((e) => e.id === r.element_id);
              return (
                <InspectionRequestCard
                  key={r.id}
                  request={r}
                  elementTitle={el ? (el.code ? `${el.code} — ${el.title}` : el.title) : null}
                  elementHasRd={!!el?.position_id}
                  reviewer={reviewer}
                />
              );
            })}
          </List>
        )}
      </Section>

      <Section>
        <SectionLabel>Конструктив</SectionLabel>
        {!elState && elements.length > 0 && (
          <SectionTools>
            <Input
              type="search"
              aria-label="Поиск по конструктиву"
              placeholder="Найти по шифру, наименованию, секции, осям или отметке…"
              value={elementSearch}
              onChange={(event) => {
                setElementSearch(event.target.value);
                setElementPageNumber(1);
              }}
            />
          </SectionTools>
        )}
        {reviewer && (
          <SectionTools>
            {site && <FieldRdImportButton key={id} siteId={id} titulId={site.titul_id} />}
          </SectionTools>
        )}
        {elState ? (
          <Muted>{elState}</Muted>
        ) : elements.length === 0 ? (
          <Muted>
            Элементов нет.
            {reviewer ? ' Добавьте их из РД — к ним прораб привяжет объёмы и заявки.' : ''}
          </Muted>
        ) : filteredElements.length === 0 ? (
          <Muted>По вашему поиску элементы не найдены.</Muted>
        ) : (
          <>
            <RegistryPagination aria-label="Страницы конструктива">
              <span>
                Элементы {elementPage.start}–{elementPage.end} из {filteredElements.length}
                {elementSearch.trim() ? ` (всего ${elements.length})` : ''}
                {' · '}Страница {elementPage.page} из {elementPage.pages}
              </span>
              <SectionTools>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={elementPage.page === 1}
                  onClick={() => setElementPageNumber(elementPage.page - 1)}
                >
                  Назад
                </Button>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={elementPage.page === elementPage.pages}
                  onClick={() => setElementPageNumber(elementPage.page + 1)}
                >
                  Далее
                </Button>
              </SectionTools>
            </RegistryPagination>
            <List>
              {elementPage.items.map((el) => (
                <Card key={el.id}>
                  <RowHead>
                    <RowTitle>{el.code ? `${el.code} — ${el.title}` : el.title}</RowTitle>
                    {/* Статус считает программа по данным (КR7); «принят» и ручной статус
                      ставит проверяющий ПТО — подсказка говорит, чей статус. */}
                    <Badge $tone={elementStatusTone(el.status)} title={elementStatusHint(el)}>
                      {elementStatusLabel(el.status)}
                      {elementStatusManual(el) ? ' · вручную' : ''}
                    </Badge>
                  </RowHead>
                  <RowMeta>
                    {[el.section, el.titul, el.axes, el.marks].filter(Boolean).join(' · ') || '—'}
                  </RowMeta>
                  <Flags>
                    <Badge $tone={el.position_id ? 'info' : 'warning'}>
                      {el.position_id
                        ? 'привязан к объёму ПТО'
                        : 'нет позиции РД — сверки не будет'}
                    </Badge>
                    {el.needs_review && <Badge $tone="warning">нужна проверка</Badge>}
                  </Flags>
                  {reviewer && <ElementStatusControls element={el} reviewer={reviewer} />}
                  {reviewer && <ElementPositionPicker siteId={id} element={el} />}
                </Card>
              ))}
            </List>
          </>
        )}
      </Section>

      <Section>
        <SectionLabel>Личные сменные рапорты</SectionLabel>
        <Muted>
          {titulLabel(titulsQ.currentData?.tituls ?? [], site?.titul_id)} · {site?.name}
        </Muted>
        {siteWriter && (
          <CreatePersonalReport key={id} siteId={id} timezone={site?.timezone ?? 'Europe/Moscow'} />
        )}
        {reviewer && (
          <InlineActions>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => {
                setOnlyMine((v) => !v);
                setReportOffset(0);
              }}
            >
              {onlyMine ? 'Показать всех прорабов' : 'Только мои рапорты'}
            </Button>
          </InlineActions>
        )}
        {repState ? (
          <Muted>{repState}</Muted>
        ) : reports.length === 0 ? (
          <Muted>Рапортов нет.</Muted>
        ) : (
          <List>
            {reports.map((rep) => (
              <ReportCard key={rep.id} to={`/agents/field/${id}/reports/${rep.id}`}>
                <RowHead>
                  <RowTitle>
                    {fmtFieldDate(rep.shift_date)} · {rep.shift_type === 'night' ? 'ночь' : 'день'}
                  </RowTitle>
                  <Badge $tone={reportStatusTone(rep.status)}>
                    {reportStatusLabel(rep.status)}
                  </Badge>
                </RowHead>
                <RowMeta>
                  Автор: {reportAuthorLabel(rep, membersQ.currentData?.members ?? [], userId)}
                </RowMeta>
                {/* «Возврат: …» — только у рапорта, который сейчас у прораба на доработке:
                    у пересданного комментарий прошлого возврата виден в его карточке. */}
                {(rep.note || (rep.status === 'returned' && rep.return_comment)) && (
                  <RowMeta>
                    {rep.note && (
                      <>
                        {rep.note}
                        <br />
                      </>
                    )}
                    {rep.status === 'returned' && rep.return_comment && (
                      <>Возврат: {rep.return_comment}</>
                    )}
                  </RowMeta>
                )}
                {rep.voice_transcript && (
                  <Flags>
                    <Badge $tone="info">голосовой</Badge>
                  </Flags>
                )}
              </ReportCard>
            ))}
          </List>
        )}
        <InlineActions>
          <Button
            $variant="ghost"
            $size="sm"
            disabled={reportOffset === 0 || repQ.isFetching}
            onClick={() => setReportOffset((n) => Math.max(0, n - 25))}
          >
            Предыдущие рапорты
          </Button>
          <span>Всего: {repQ.currentData?.total ?? 0}</span>
          <Button
            $variant="ghost"
            $size="sm"
            disabled={repQ.isFetching || reportOffset + 25 >= (repQ.currentData?.total ?? 0)}
            onClick={() => setReportOffset((n) => n + 25)}
          >
            Следующие рапорты
          </Button>
        </InlineActions>
      </Section>

      {(facts.length > 0 || factsQ.isError) && (
        <Section>
          <SectionLabel>Факты объёма (сверка ПТО)</SectionLabel>
          {factsQ.isError ? (
            <Muted>
              {apiLoadErrorText(
                factsQ.error,
                'Не удалось загрузить факты объёма.',
                'Нет доступа к этому разделу.',
              )}
            </Muted>
          ) : (
            <List>
              {facts.map((f) => {
                const rv = factReconcileView(f.reconcile, { elementBound: !!f.element_id });
                // У принятого — принятый объём (правка ПТО) и «заявлено N» (B9).
                const qv = factQtyView(f);
                return (
                  <ReportCard key={f.id} to={`/agents/field/${id}/volume-facts/${f.id}`}>
                    <RowHead>
                      <RowTitle>
                        {f.work_item_code ? `${f.work_item_code} — ${f.name}` : f.name}
                      </RowTitle>
                      <Badge $tone={volumeFactStatusTone(f.status)}>
                        {volumeFactStatusLabel(f.status)}
                      </Badge>
                    </RowHead>
                    <RowMeta>
                      {qv.primary}
                      {qv.secondary && ` · ${qv.secondary}`}
                    </RowMeta>
                    <Flags>
                      <Badge $tone={rv.tone}>{rv.label}</Badge>
                      {/* «Нужна проверка» — только пока по факту нет решения ПТО. */}
                      {factNeedsReview(f) && <Badge $tone="warning">нужна проверка</Badge>}
                      {factReconcileFlags(f.reconcile, f.status, { reviewer }).map((fl) => (
                        <Badge key={fl.label} $tone={fl.tone} title={fl.hint}>
                          {fl.label}
                        </Badge>
                      ))}
                    </Flags>
                  </ReportCard>
                );
              })}
            </List>
          )}
        </Section>
      )}

      <Section>
        <SectionLabel>Задачи и предписания</SectionLabel>
        {/* Задачи прорабу ставит ведущий площадку (ПТО/администратор), не сам прораб. */}
        {canManage && (
          <VkForm
            onSubmit={(e) => {
              e.preventDefault();
              const text = taskText.trim();
              if (!text) return;
              setTaskError(null);
              createTask({
                siteId: id,
                source: taskSource,
                text,
                requiresPhoto: taskRequiresPhoto,
              })
                .unwrap()
                .then(() => {
                  setTaskText('');
                  setTaskRequiresPhoto(false);
                })
                .catch((err: unknown) =>
                  setTaskError(apiErrorText(err, 'Не удалось создать задачу.')),
                );
            }}
          >
            <Input
              value={taskText}
              onChange={(e) => setTaskText(e.target.value)}
              placeholder="Текст задачи/предписания прорабу"
              aria-label="Текст задачи"
            />
            <Chip
              type="button"
              $active={taskSource === 'stroycontrol'}
              onClick={() => setTaskSource(taskSource === 'stroycontrol' ? 'pto' : 'stroycontrol')}
              title="Источник: ПТО или стройконтроль"
            >
              {taskSource === 'stroycontrol' ? 'стройконтроль' : 'ПТО'}
            </Chip>
            <Chip
              type="button"
              $active={taskRequiresPhoto}
              onClick={() => setTaskRequiresPhoto((v) => !v)}
              title="Выполнение потребует приложить фото"
            >
              фотоответ
            </Chip>
            <Button type="submit" $variant="secondary" $size="sm" disabled={taskCreating}>
              Создать
            </Button>
          </VkForm>
        )}
        {taskError && <ErrorNote>{taskError}</ErrorNote>}
        {taskState ? (
          <Muted style={{ marginTop: 12 }}>{taskState}</Muted>
        ) : tasks.length === 0 ? (
          <Muted style={{ marginTop: 12 }}>Задач нет.</Muted>
        ) : (
          <List style={{ marginTop: 12 }}>
            {tasks.map((t) => (
              <Card key={t.id}>
                <RowHead>
                  <RowTitle>
                    {t.source === 'stroycontrol' ? 'Стройконтроль' : 'ПТО'}
                    {t.due_date ? ` · срок ${fmtFieldDate(t.due_date)}` : ''}
                  </RowTitle>
                  <Badge $tone={taskStatusTone(t.status)}>{taskStatusLabel(t.status)}</Badge>
                </RowHead>
                <RowMeta>
                  {t.text}
                  {t.decline_reason && (
                    <>
                      <br />
                      Отклонена: {t.decline_reason}
                    </>
                  )}
                  {t.response_comment && (
                    <>
                      <br />
                      Прораб: {t.response_comment}
                    </>
                  )}
                </RowMeta>
                <Flags>{t.requires_photo && <Badge $tone="info">нужен фотоответ</Badge>}</Flags>
                {siteWriter && (t.status === 'new' || t.status === 'in_progress') && (
                  <InlineActions>
                    <Button
                      type="button"
                      $variant="ghost"
                      $size="sm"
                      disabled={taskSaving}
                      onClick={() => void closeTask(t.id)}
                      title="Закрыть выполненную задачу (за прораба — HITL офиса)"
                    >
                      Выполнена
                    </Button>
                  </InlineActions>
                )}
                {taskCloseError?.id === t.id && <ErrorNote>{taskCloseError.text}</ErrorNote>}
              </Card>
            ))}
          </List>
        )}
      </Section>

      <Section>
        <SectionLabel>Фотофиксация</SectionLabel>
        <label>
          Рапорт на текущей странице списка:{' '}
          <select
            aria-label="Фото рапорта"
            value={photoReport}
            onChange={(e) => {
              setPhotoReport(e.target.value);
              setPhotoOffset(0);
            }}
          >
            <option value="">Все рапорты</option>
            {reports.map((rep) => (
              <option key={rep.id} value={rep.id}>
                {fmtFieldDate(rep.shift_date)} · {rep.shift_type === 'night' ? 'ночь' : 'день'} ·{' '}
                {reportAuthorLabel(rep, membersQ.currentData?.members ?? [], userId)}
              </option>
            ))}
          </select>
        </label>
        <FilterChips>
          <Chip
            type="button"
            $active={photoCategory === null}
            onClick={() => {
              setPhotoCategory(null);
              setPhotoOffset(0);
            }}
          >
            все
          </Chip>
          {PHOTO_CATEGORIES.map((c) => (
            <Chip
              key={c}
              type="button"
              $active={photoCategory === c}
              onClick={() => {
                setPhotoCategory(c);
                setPhotoOffset(0);
              }}
            >
              {photoCategoryLabel(c)}
            </Chip>
          ))}
        </FilterChips>
        {photoState ? (
          <Muted>{photoState}</Muted>
        ) : photos.length === 0 ? (
          <Muted>Фото нет.</Muted>
        ) : (
          <>
            <PhotoGrid>
              {photos.map((p) => {
                const badges = photoBadges(p, photoProblems[p.id]);
                return (
                  <div key={p.id}>
                    <FieldPhotoThumb
                      photoId={p.id}
                      alt={`${photoCategoryLabel(p.category)} · ${fmtFieldDate(p.taken_at ?? p.server_received_at)}`}
                      onProblem={(problem) =>
                        setPhotoProblems((prev) =>
                          prev[p.id] === problem ? prev : { ...prev, [p.id]: problem },
                        )
                      }
                    />
                    <PhotoMeta>
                      {photoCategoryLabel(p.category)} ·{' '}
                      {fmtFieldDate(p.taken_at ?? p.server_received_at)}
                      {p.series_order !== null && <> · кадр {p.series_order}</>}
                      {p.source === 'gallery' && <> · из галереи</>}
                      <br />
                      Автор: {reportAuthorLabel(p, membersQ.currentData?.members ?? [], userId)}
                      {p.report_id && (
                        <>
                          {' '}
                          · <Link to={`/agents/field/${id}/reports/${p.report_id}`}>К рапорту</Link>
                        </>
                      )}
                    </PhotoMeta>
                    {badges.length > 0 && (
                      <PhotoFlags>
                        {badges.map((b) => (
                          <Badge key={b.label} $tone={b.tone} title={b.hint}>
                            {b.label}
                          </Badge>
                        ))}
                      </PhotoFlags>
                    )}
                  </div>
                );
              })}
            </PhotoGrid>
            <InlineActions>
              <Button
                $variant="ghost"
                $size="sm"
                disabled={photoOffset === 0 || photoQ.isFetching}
                onClick={() => setPhotoOffset((n) => Math.max(0, n - PHOTO_PAGE))}
              >
                Предыдущие фото
              </Button>
              <span>Всего: {photoQ.currentData?.total ?? 0}</span>
              <Button
                $variant="ghost"
                $size="sm"
                disabled={
                  photoQ.isFetching || photoOffset + PHOTO_PAGE >= (photoQ.currentData?.total ?? 0)
                }
                onClick={() => setPhotoOffset((n) => n + PHOTO_PAGE)}
              >
                Следующие фото
              </Button>
            </InlineActions>
          </>
        )}
      </Section>

      <Section>
        <SectionLabel>Приёмка материалов (ТТН и паспорта качества)</SectionLabel>
        {siteWriter && (
          <Card style={{ marginBottom: 16 }}>
            <RowTitle style={{ marginBottom: 8 }}>Загрузить фото / скан ТТН или паспорта (L-5 OCR)</RowTitle>
            <InlineActions>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              />
              <select
                value={receiptElementId}
                onChange={(e) => setReceiptElementId(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6 }}
              >
                <option value="">— без привязки к конструктиву —</option>
                {elements.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.code ? `${el.code} · ${el.title}` : el.title}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                disabled={!receiptFile || receiptUploading}
                onClick={async () => {
                  if (!receiptFile) return;
                  setReceiptMsg(null);
                  try {
                    await uploadReceipt({
                      siteId: id,
                      file: receiptFile,
                      elementId: receiptElementId || undefined,
                    }).unwrap();
                    setReceiptFile(null);
                    setReceiptElementId('');
                    setReceiptMsg({
                      ok: true,
                      text: 'ТТН принята и поставлена в очередь распознавания (L-5).',
                    });
                  } catch (e) {
                    setReceiptMsg({
                      ok: false,
                      text: apiErrorText(e, 'Не удалось загрузить ТТН/паспорт.'),
                    });
                  }
                }}
              >
                {receiptUploading ? 'Загрузка…' : 'Загрузить и распознать'}
              </Button>
            </InlineActions>
            {receiptMsg && (
              <RowMeta style={{ color: receiptMsg.ok ? '#27ae60' : '#c0392b', marginTop: 8 }}>
                {receiptMsg.text}
              </RowMeta>
            )}
          </Card>
        )}
        {receiptsState ? (
          <Muted>{receiptsState}</Muted>
        ) : receipts.length === 0 ? (
          <Muted>Приёмок материалов по площадке пока нет.</Muted>
        ) : (
          <List>
            {receipts.map((r) => (
              <Card key={r.id}>
                <RowHead>
                  <RowTitle>
                    {r.nomenclature || 'Материал (распознаётся)'}{' '}
                    {r.ttn_no ? `· ТТН № ${r.ttn_no}` : ''}
                  </RowTitle>
                  <Badge $tone={r.status === 'confirmed' ? 'success' : 'warning'}>
                    {r.status === 'confirmed' ? 'подтверждена' : 'черновик'}
                  </Badge>
                </RowHead>
                <RowMeta>
                  Поставщик: {r.supplier || '—'} · Дата ТТН: {r.ttn_date || fmtFieldDate(r.created_at)}
                  <br />
                  Объём поставки: {r.qty_delivered ? `${r.qty_delivered} ${r.unit ?? ''}` : '—'}
                  {r.provenance === 'ocr_pending' && (
                    <>
                      {' '}
                      · <Badge $tone="info">распознаётся L-5…</Badge>
                    </>
                  )}
                  {r.provenance === 'failed' && (
                    <>
                      {' '}
                      · <Badge $tone="danger">сбой OCR</Badge>
                    </>
                  )}
                  {r.needs_review && (
                    <>
                      {' '}
                      · <Badge $tone="warning">нужна проверка</Badge>
                    </>
                  )}
                </RowMeta>
                {siteWriter && (
                  <InlineActions style={{ marginTop: 10 }}>
                    <select
                      value={r.lot_id ?? ''}
                      disabled={attachingPassport}
                      onChange={async (e) => {
                        const lotId = e.target.value || null;
                        setReceiptMsg(null);
                        try {
                          await attachPassport({ id: r.id, lot_id: lotId }).unwrap();
                        } catch (err) {
                          setReceiptMsg({
                            ok: false,
                            text: apiErrorText(err, 'Не удалось привязать партию качества МТО.'),
                          });
                        }
                      }}
                      style={{ padding: '4px 8px', borderRadius: 6, fontSize: 12 }}
                    >
                      <option value="">— партия МТО (паспорт качества) —</option>
                      {mtoLots.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.lot_no ? `${l.lot_no} · ` : ''}
                          {l.nomenclature}
                        </option>
                      ))}
                    </select>
                    {r.status !== 'confirmed' && foreman && (
                      <Button
                        type="button"
                        $variant="secondary"
                        $size="sm"
                        disabled={confirmingReceipt}
                        onClick={async () => {
                          setReceiptMsg(null);
                          try {
                            await confirmReceipt(r.id).unwrap();
                          } catch (err) {
                            setReceiptMsg({
                              ok: false,
                              text: apiErrorText(err, 'Не удалось подтвердить приёмку ТТН.'),
                            });
                          }
                        }}
                      >
                        Подтвердить ТТН
                      </Button>
                    )}
                    <Button
                      type="button"
                      $variant="ghost"
                      $size="sm"
                      disabled={deletingReceipt}
                      onClick={async () => {
                        setReceiptMsg(null);
                        try {
                          await deleteReceipt(r.id).unwrap();
                        } catch (err) {
                          setReceiptMsg({
                            ok: false,
                            text: apiErrorText(err, 'Не удалось удалить приёмку ТТН.'),
                          });
                        }
                      }}
                    >
                      Удалить
                    </Button>
                  </InlineActions>
                )}
              </Card>
            ))}
          </List>
        )}
      </Section>

      <Section>
        <SectionLabel>Аномалии</SectionLabel>
        {anState ? (
          <Muted>{anState}</Muted>
        ) : anomalies.length === 0 ? (
          <Muted>Аномалий нет.</Muted>
        ) : (
          <List>
            {anomalies.map((a) => {
              const llm = anomalyLlmView(a);
              // Замечания модели по кадру — одной аномалией на фото (КW4): списком.
              const flags = anomalyFlags(a);
              // Решение: окончательное — только проверяющего ПТО; прораб лишь
              // принимает находку к сведению (КR1).
              const dv = anomalyDecisionView(a);
              const actions = anomalyDecisionActions(a, { reviewer, siteWriter });
              return (
                <Card key={a.id}>
                  <RowHead>
                    <RowTitle>{a.title}</RowTitle>
                    <Badge $tone={anomalySeverityTone(a.severity)}>
                      {anomalySeverityLabel(a.severity)}
                    </Badge>
                  </RowHead>
                  <RowMeta>
                    {flags.length > 1 ? (
                      <FlagList>
                        {flags.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </FlagList>
                    ) : (
                      (flags[0] ?? a.description) && (
                        <>
                          {flags[0] ?? a.description}
                          <br />
                        </>
                      )
                    )}
                    {anomalyKindLabel(a.kind)} ·{' '}
                    {a.detected_by === 'llm' ? 'выявлено ИИ' : 'детектор'} ·{' '}
                    {fmtFieldDate(a.created_at)}
                  </RowMeta>
                  {/* Мнение фильтра шума (L-6): модель лишь предлагает отклонить —
                      решение за человеком, поэтому причина видна рядом с кнопками. */}
                  {llm.noise && (
                    <LlmNote>
                      ИИ считает находку шумом
                      {llm.noise.reason ? `: ${llm.noise.reason}` : ' (причину не назвал)'}.
                      {!dv.final &&
                        (reviewer
                          ? ' Решение за вами — сверьте с данными смены.'
                          : ' Решение принимает проверяющий ПТО.')}
                    </LlmNote>
                  )}
                  {dv.by && <RowMeta>{dv.by}</RowMeta>}
                  <Flags>
                    <Badge $tone={dv.tone} title={dv.hint}>
                      {dv.label}
                    </Badge>
                    {a.detected_by === 'llm' && !a.decision && (
                      <Badge
                        $tone="warning"
                        title="Вывод модели не проверен человеком — сверьте с фото и данными смены"
                      >
                        проверьте вывод ИИ
                      </Badge>
                    )}
                    {llm.noise && (
                      <Badge
                        $tone="info"
                        title={`Фильтр шума ИИ (${a.llm_filter_version ?? 'L-6'}) предлагает отклонить находку${
                          llm.noise.reason ? `: ${llm.noise.reason}` : ''
                        }. Сам он её не закрывает.`}
                      >
                        ИИ: вероятно шум
                      </Badge>
                    )}
                    {llm.reviewed && !llm.noise && (
                      <Badge
                        $tone="neutral"
                        title={`Фильтр ИИ (${a.llm_filter_version}) пересмотрел важность находки — это не подтверждение. Сверьте с данными смены.`}
                      >
                        важность пересмотрена ИИ
                      </Badge>
                    )}
                    {a.needs_review && <Badge $tone="warning">нужна проверка</Badge>}
                  </Flags>
                  {actions.length > 0 && (
                    <InlineActions>
                      {actions.map((act) => (
                        <Button
                          key={act.decision}
                          type="button"
                          $variant={act.decision === 'acknowledged' ? 'secondary' : 'ghost'}
                          $size="sm"
                          disabled={decisionSaving}
                          title={act.title}
                          onClick={() => void decideAnomaly(a.id, act.decision)}
                        >
                          {act.label}
                        </Button>
                      ))}
                    </InlineActions>
                  )}
                  {anomalyError?.id === a.id && <ErrorNote>{anomalyError.text}</ErrorNote>}
                </Card>
              );
            })}
          </List>
        )}
      </Section>
    </>
  );
}
